import React, { useState, useEffect, useMemo } from 'react';
import { InputNumber, Button, Collapse, Select } from 'antd';
import KeyValueSelect from '@shared/components/KeyValueSelect';
import StyledSelect from '@shared/components/StyledSelect';
import { appTracker } from '@src/shared/utils/sensor/apptracker';
import { FilterContainer, SelectContainer, FilterRow } from '../../styled';
import { UNIT_OPTIONS } from '@src/shared/constants/analysis';
import { isEqual, debounce } from 'lodash';

interface LeftFiltersProps {
  displayType: 'patterns' | 'sankey';
  eventOptions: Array<{ label: React.ReactNode; value: string; name?: string }>;
  businessGoal: string;
  setBusinessGoal: (value: string) => void;
  selectedNodes: string[];
  setSelectedNodes: (nodes: string[]) => void;
  startEvents: string[];
  setStartEvents: (events: string[]) => void;
  includeMatchType: 'every' | 'any';
  setIncludeMatchType: (type: 'every' | 'any') => void;
  includeEvents: string[];
  setIncludeEvents: (events: string[]) => void;
  excludeEvents: string[];
  setExcludeEvents: (events: string[]) => void;
  topPaths: number;
  setTopPaths: (value: number) => void;
  timeout: number;
  setTimeout: (value: number) => void;
  timeoutUnit: string;
  setTimeoutUnit: (value: string) => void;
  hiddenEvents: string[];
  setHiddenEvents: (events: string[]) => void;
}

const LeftFilters: React.FC<LeftFiltersProps> = ({
  displayType,
  eventOptions,
  businessGoal,
  setBusinessGoal,
  startEvents,
  setStartEvents,
  includeMatchType,
  setIncludeMatchType,
  includeEvents,
  setIncludeEvents,
  excludeEvents,
  setExcludeEvents,
  topPaths,
  setTopPaths,
  timeout,
  setTimeout,
  timeoutUnit,
  setTimeoutUnit,
  hiddenEvents,
  setHiddenEvents,
}) => {
  // Local state for topPaths input to avoid triggering queries on every change
  const [topPathsInput, setTopPathsInput] = useState<number | null>(topPaths);

  // Sync local state when topPaths prop changes externally
  useEffect(() => {
    setTopPathsInput(topPaths);
  }, [topPaths]);

  const debouncedReportSearch = useMemo(
    () =>
      debounce((value: string) => {
        appTracker({
          name: 'Milestone Event Search',
          data: {
            search_keyword: value,
          },
        });
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedReportSearch.cancel();
    };
  }, [debouncedReportSearch]);

  const handleTopPathsCommit = () => {
    let nextValue = topPathsInput;
    if (!nextValue) {
      nextValue = 10;
    }
    if (displayType === 'patterns' && nextValue > 10) {
      nextValue = 10;
    }
    if (nextValue < 1) {
      nextValue = 1;
    }

    if (nextValue !== topPaths) {
      setTopPaths(nextValue);
      appTracker({
        name: 'Journey Explorer Top Paths Change',
        data: {
          topPaths: nextValue,
        },
      });
    }
  };

  const renderDurationLimitSection = () => {
    return (
      <div style={{ display: 'flex', gap: 16 }}>
        <FilterRow>
          <span className="label">Expected time to completion:</span>
          <InputNumber
            className="number-input"
            min={1}
            style={{ width: 70 }}
            value={timeout}
            onChange={(value) => {
              setTimeout(value || 1);
            }}
          />
        </FilterRow>
        <Select
          style={{ width: 100 }}
          options={UNIT_OPTIONS}
          value={timeoutUnit}
          onChange={(value) => {
            setTimeoutUnit(value || 'h');
          }}
        />
      </div>
    );
  };

  return (
    <FilterContainer>
      <Collapse
        defaultActiveKey={['1', '2', '3']}
        ghost
        items={[
          {
            key: '1',
            label: <div className="header">Select end with events</div>,
            className: 'dimension-filter',
            children: (
              <>
                <SelectContainer>
                  <StyledSelect
                    label="Milestone:"
                    valueOptions={eventOptions}
                    selectedValues={businessGoal ? [businessGoal] : []}
                    onChange={(values) => {
                      setBusinessGoal(values as string);
                    }}
                    onSelect={(value) => {
                      appTracker({
                        name: 'Milestone Event Selected',
                        data: {
                          milestone: value,
                        },
                      });
                    }}
                    onSearch={(value) => {
                      debouncedReportSearch(value);
                    }}
                    mode="single"
                    getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                    backgroundColor="#F2F6FF"
                  />
                </SelectContainer>
              </>
            ),
          },
          {
            key: '2',
            label: <div className="header">Select critical events</div>,
            className: 'dimension-filter',
            children: (
              <>
                <SelectContainer>
                  <StyledSelect
                    label="Start With:"
                    valueOptions={eventOptions}
                    selectedValues={startEvents}
                    onChange={(values) => {
                      setStartEvents(values as string[]);
                    }}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                  />
                </SelectContainer>

                <SelectContainer>
                  <KeyValueSelect
                    keyOptions={[
                      { label: 'Include Any:', value: 'any' },
                      { label: 'Include Every:', value: 'every' },
                    ]}
                    keepValuesOnKeyChange={true}
                    valueOptions={eventOptions}
                    selectedKey={includeMatchType}
                    selectedValues={includeEvents}
                    onChange={(key, values, operator) => {
                      setIncludeMatchType(key as 'every' | 'any');
                      if (isEqual(values, includeEvents)) {
                        return;
                      }
                      setIncludeEvents(values);
                    }}
                    getPopupContainer={(triggerNode) => document.body}
                    showOperator={false}
                    mode="multiple"
                  />
                </SelectContainer>

                <SelectContainer>
                  <StyledSelect
                    label="Exclude:"
                    valueOptions={eventOptions}
                    selectedValues={excludeEvents}
                    onChange={(values) => {
                      setExcludeEvents(values as string[]);
                    }}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                  />
                </SelectContainer>
                {displayType === 'sankey' && (
                  <SelectContainer>
                    <StyledSelect
                      label="Hide:"
                      valueOptions={eventOptions.filter(
                        (event) =>
                          event.value !== businessGoal && !startEvents.includes(event.value),
                      )}
                      selectedValues={hiddenEvents}
                      onChange={(values) => {
                        setHiddenEvents(values as string[]);
                      }}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                    />
                  </SelectContainer>
                )}
              </>
            ),
          },
          {
            key: '3',
            label: <div className="header">Limit</div>,
            className: 'dimension-filter',
            children: (
              <>
                <FilterRow>
                  <span className="label">Top:</span>
                  <InputNumber
                    className="number-input"
                    min={1}
                    value={topPathsInput}
                    onChange={(value) => {
                      setTopPathsInput(value);
                    }}
                    onBlur={handleTopPathsCommit}
                    size="small"
                  />
                </FilterRow>
                {displayType !== 'patterns' && renderDurationLimitSection()}
              </>
            ),
          },
        ]}
      />
    </FilterContainer>
  );
};

export default LeftFilters;
